import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import { useT, useLang } from '../../lib/i18n.jsx';
import { get } from '../../lib/api.js';
import { useAsync, initials } from '../../lib/hooks.js';
import { daysUntil, formatDate } from '../../lib/format.js';
import { errorMessage } from '../../lib/errors.js';
import AddDomainModal from './AddDomainModal.jsx';
import DomainDetail from './DomainDetail.jsx';
import DomainOrders from './DomainOrders.jsx';

export default function DomainsPage() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const params = useParams();
  const [search, setSearch] = useSearchParams();

  const { data: domains, loading, error, reload } = useAsync(() => get('/dns/domains'), []);
  const { data: hostings } = useAsync(() => get('/hostings').catch(() => []), []);
  const { data: orders, reload: reloadOrders } = useAsync(() => get('/dns/domains/orders').catch(() => []), []);
  const returnedOrderId = /^\d+$/.test(search.get('order') || '') ? search.get('order') : null;
  const [addOpen, setAddOpen] = useState(search.get('action') === 'addDomain');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const action = search.get('action');
  useEffect(() => {
    if (action === 'addDomain') setAddOpen(true);
  }, [action]);

  // "Add DNS record" shortcut: with a single domain go straight to its DNS tab,
  // otherwise let the customer pick a domain from the list.
  const pickForDns = action === 'dns';
  useEffect(() => {
    if (pickForDns && domains && domains.length === 1) {
      navigate(`/domeinen/${domains[0].domain_name}?tab=dns`, { replace: true });
    }
  }, [pickForDns, domains, navigate]);

  const hostingMap = useMemo(() => {
    const map = new Map();
    for (const h of hostings || []) map.set(h.domain, h);
    return map;
  }, [hostings]);

  if (params.domain) {
    const dom = (domains || []).find((d) => d.domain_name === params.domain || String(d.id) === params.domain);
    if (!dom && !loading && error) {
      return (
        <main className="page">
          <button className="back-link" onClick={() => navigate('/domeinen')}>
            <Icon name="arrow-left" size={14} />
            <span style={{ marginLeft: 4 }}>{t('dd.back')}</span>
          </button>
          <div className="state-card" role="alert">
            <h3>{t('domainsPage.loadError')}</h3>
            <p>{errorMessage(error, { lang })}</p>
          </div>
        </main>
      );
    }
    if (!dom && !loading) {
      return (
        <main className="page">
          <button className="back-link" onClick={() => navigate('/domeinen')}>
            <Icon name="arrow-left" size={14} />
            <span style={{ marginLeft: 4 }}>{t('dd.back')}</span>
          </button>
          <div className="state-card">
            <h3>{t('domainsPage.notFound')}</h3>
          </div>
        </main>
      );
    }
    if (dom) {
      return (
        <DomainDetail
          key={dom.id}
          domain={dom}
          initialTab={search.get('tab') || undefined}
          onBack={() => navigate('/domeinen')}
          reload={reload}
        />
      );
    }
  }

  const list = domains || [];
  const counts = {
    all: list.length,
    active: list.filter((d) => d.status === 'active').length,
    pending: list.filter((d) => PENDING_STATUSES.includes(d.status)).length,
  };

  const filtered = list.filter((d) => {
    if (filter === 'active' && d.status !== 'active') return false;
    if (filter === 'pending' && !PENDING_STATUSES.includes(d.status)) return false;
    if (query && !d.domain_name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <main className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('dom.title')}</h1>
          <p className="page-sub">{t('dom.sub')}</p>
        </div>
        <div className="actions">
          <button className="btn-primary" onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={15} stroke={2} />
            <span style={{ marginLeft: 6 }}>{t('dom.add')}</span>
          </button>
        </div>
      </div>

      <div className="filterbar">
        <div className="search-input">
          <Icon name="search" size={15} />
          <input
            placeholder={t('domainsPage.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="chips">
          {[
            ['all', t('dom.filter.all'), counts.all],
            ['active', t('dom.filter.active'), counts.active],
            ['pending', t('domainsPage.filter.pending'), counts.pending],
          ].map(([k, label, n]) => (
            <button key={k} className="chip" aria-pressed={filter === k} onClick={() => setFilter(k)}>
              {label}
              <span style={{ marginLeft: 6, color: filter === k ? 'rgba(255,255,255,.55)' : 'var(--ink-4)' }}>{n}</span>
            </button>
          ))}
        </div>
      </div>

      <DomainOrders
        lang={lang}
        orders={orders}
        returnedOrderId={returnedOrderId}
        onDismissReturned={() => {
          search.delete('order');
          setSearch(search, { replace: true });
        }}
        onChanged={() => {
          reload();
          reloadOrders();
        }}
      />

      {pickForDns && !loading && list.length !== 1 && (
        <div className="banner" role="status">
          <div>
            <h2>{t('domainsPage.dnsPick.title')}</h2>
            <p>
              {list.length === 0
                ? t('domainsPage.dnsPick.none')
                : t('domainsPage.dnsPick.choose')}
            </p>
          </div>
          <button
            className="btn-ghost"
            style={{ padding: '8px 14px' }}
            onClick={() => {
              search.delete('action');
              setSearch(search, { replace: true });
            }}
          >
            {t('domainsPage.dismiss')}
          </button>
        </div>
      )}

      <section className="card" style={{ padding: 0 }}>
        <div className="tbl-head">
          <span>{t('dom.col.domain')}</span>
          <span>{t('dom.col.linked')}</span>
          <span>{t('dom.col.expires')}</span>
          <span>{t('dom.col.autorenew')}</span>
          <span>{t('dom.col.status')}</span>
        </div>
        {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)' }}>{t('common.loading')}</div>}
        {!loading && error && (
          <div style={{ padding: '24px 22px', textAlign: 'center', color: 'var(--danger)' }} role="alert">
            {errorMessage(error, { lang })}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ padding: '24px 22px', textAlign: 'center', color: 'var(--ink-3)' }}>
            {t('domainsPage.empty')}
          </div>
        )}
        {filtered.map((d) => {
          const hosting = d.hosting_id ? (hostings || []).find((h) => h.id === d.hosting_id) : null;
          const linkedLabel = hosting ? hosting.domain : t('domainsPage.notLinked');
          const linkedKind = hosting ? 'hosting' : 'parked';
          const daysLeft = daysUntil(d.expires_at);

          return (
            <button
              type="button"
              key={d.id}
              className="dom-row"
              onClick={() => navigate(`/domeinen/${d.domain_name}${pickForDns ? '?tab=dns' : ''}`)}
            >
              <div className="dn">
                <div className="fav">{initials(d.domain_name.split('.')[0])}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="domain" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.domain_name}
                  </div>
                  <div className="sub">{d.registrar || 'Nextname'}</div>
                </div>
              </div>
              <LinkedCell kind={linkedKind} label={linkedLabel} />
              <div className="expiry">
                {d.expires_at ? formatDate(d.expires_at, lang) : '—'}
                {daysLeft != null && (
                  <div className="d">{daysLabel(daysLeft, t)}</div>
                )}
              </div>
              <div>
                <span
                  className="switch"
                  aria-checked={d.auto_renew ? 'true' : 'false'}
                  role="img"
                  aria-label={d.auto_renew ? t('domainsPage.autoRenew.on') : t('domainsPage.autoRenew.off')}
                />
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className={'status-pill ' + statusClass(d.status)}>
                  <span className="dot" />
                  {statusLabel(d.status, t)}
                </span>
              </div>
            </button>
          );
        })}
      </section>

      {addOpen && (
        <AddDomainModal
          lang={lang}
          existing={list.map((d) => d.domain_name)}
          onClose={() => {
            setAddOpen(false);
            if (search.get('action')) {
              search.delete('action');
              setSearch(search, { replace: true });
            }
          }}
          onCreated={() => {
            setAddOpen(false);
            reload();
          }}
          onOrdersChanged={reloadOrders}
        />
      )}
    </main>
  );
}

function statusClass(s) {
  if (s === 'active') return '';
  if (s === 'expired' || s === 'cancelled' || s === 'suspended') return 'is-danger';
  return 'is-warn';
}

const DOMAIN_STATUSES = ['active', 'pending', 'transferring', 'expired', 'suspended', 'cancelled'];
// The API calls an inbound transfer in progress `transfer_pending`.
const PENDING_STATUSES = ['pending', 'transferring', 'transfer_pending'];

function statusLabel(s, t) {
  if (s === 'transfer_pending') return t('domainsPage.status.transferring');
  if (DOMAIN_STATUSES.includes(s)) return t(`domainsPage.status.${s}`);
  return s || t('domainsPage.status.unknown');
}

function LinkedCell({ kind, label }) {
  const icon = kind === 'hosting' ? 'server' : kind === 'forward' ? 'arrow-right' : 'globe';
  return (
    <span className="linked">
      <span className="li"><Icon name={icon} size={13} /></span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
    </span>
  );
}

function daysLabel(days, t) {
  if (days < 0) return t('domainsPage.days.expired');
  if (days === 0) return t('domainsPage.days.today');
  return t(days === 1 ? 'domainsPage.days.one' : 'domainsPage.days.other', { n: days });
}
